// =============================================================
// FLOW (WAVE 5): People & access — full-depth port of the prototype SecAccess
// (scripts/perpetuum-desktop-sections6.jsx) over the REAL /api/v1. Four tabs,
// RBAC server-enforced:
//   - Users        → /users (list + create/edit dialog + activate/deactivate +
//                    multi-group assignment, lang + department)
//   - Permissions  → /user-groups: the EDITABLE access-level-per-module matrix +
//                    card-tab visibility, persisted to user_groups.permissions
//                    JSONB via the core groupFromLevels/level ladder. Round-trips.
//   - Companies    → /companies (list + create/edit: name, currency)
//   - Departments  → /departments (indented hierarchy via parentDepartmentId +
//                    create/edit/delete with parent selection; confirm-on-delete)
//
// The permission matrix uses the SAME core RBAC model the server enforces —
// PERM_MODULES, AccessLevel 0..3, levelName, cardTabVisible, groupFromLevels.
// No parallel permission model is hand-rolled.
//
// HONEST API NOTES — only fields the live schema (apps/server/src/db/schema.ts)
// carries are written; the generic CRUD insert persists exactly the table's
// columns. So, relative to the prototype mocks, we OMIT:
//   - users: an INITIAL PASSWORD. The schema column is `password_hash` and the
//     generic POST /users does NOT hash a plaintext `password`; sending one
//     would be dropped (or worse, stored unhashed). We never send it and note
//     that passwords are set via a server-issued reset. We also omit the
//     prototype's `performer` / `email-notify` / `can-login` booleans (no such
//     columns) — sign-in capability is governed by isActive + group RBAC.
//   - companies: address / PDF-footer / enabledModuleIds are managed elsewhere
//     (the admin console). Here we keep name + currency, the two list columns.
//   - departments: the prototype's "asset count" is not derivable from the
//     departments endpoint alone, so it is omitted (no fabricated counts).
// =============================================================

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ACCESS,
  PERM_MODULES,
  cardTabVisible,
  groupFromLevels,
  levelName,
  type AccessLevel,
  type GroupPermissions,
  type PermModule,
} from "@perpetuum/core";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import {
  useCollection,
  useCreateRow,
  useDeleteRow,
  useUpdateGroupPermissions,
  useUpdateRow,
  type Row,
} from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";

type Tab = "users" | "perms" | "companies" | "depts";

const MODULE_LABEL: Record<PermModule, string> = {
  dev: "Devices & locations",
  tasks: "Tasks & work orders",
  spares: "Spares & inventory",
  downtime: "Downtimes",
  reports: "Reports",
  admin: "Settings & admin",
};

// Selectable levels per module. The core ladder is 0..3; `levelName` collapses
// the admin module's 2→"Full" so the rendered labels match the prototype.
const LEVELS: AccessLevel[] = [ACCESS.NONE, ACCESS.VIEW, ACCESS.EDIT, ACCESS.FULL];

const LANGS = ["en", "lv", "ru"] as const;
const LANG_LABEL: Record<(typeof LANGS)[number], string> = { en: "English", lv: "Latviešu", ru: "Русский" };

const selectStyle = {
  border: 0,
  background: "none",
  outline: "none",
  font: "inherit",
  color: "inherit",
  width: "100%",
} as const;

export function Access() {
  const t = useScopedT();
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>("users");
  const canWrite = can("admin", "write");
  // RBAC (Wave-8 fix): the admin module now uses the full None/View/Edit/Full
  // ladder, and Super-admin seeds admin:Full(3) — so `can("admin","delete")` is
  // REACHABLE and authoritative. Department delete gates on it directly; the
  // server enforces the same matrix, so a write-only admin (admin:Edit) sees the
  // delete affordance hidden here AND a 403 if they call the route anyway.
  const canDelete = can("admin", "delete");

  return (
    <div>
      <h2 className="d-h2">{t("access.title")}</h2>
      <p className="d-sub">{t("access.sub")}</p>

      <div className="view-seg seg" role="tablist" aria-label={t("access.title")} style={{ marginBottom: 18 }}>
        {(
          [
            ["users", t("access.tab.users")],
            ["perms", t("access.tab.perms")],
            ["companies", t("access.tab.companies")],
            ["depts", t("access.tab.depts")],
          ] as [Tab, string][]
        ).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} aria-pressed={tab === k} data-testid={`access-tab-${k}`} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersTab canWrite={canWrite} />}
      {tab === "perms" && <PermsTab canWrite={canWrite} />}
      {tab === "companies" && <CompaniesTab canWrite={canWrite} />}
      {tab === "depts" && <DeptsTab canWrite={canWrite} canDelete={canDelete} />}
    </div>
  );
}

// ---- Modal shell (focus-trapped + Esc) ------------------------------------

function Modal({
  title,
  icon = ICONS.users,
  onClose,
  children,
  testId,
  width = 480,
}: {
  title: string;
  icon?: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  width?: number;
}) {
  // Focus the first focusable control ONCE on mount (an inline ref callback
  // would re-fire every render and steal focus mid-typing) + trap Tab.
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const focusable = node.querySelector<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, []);
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal"
        ref={ref}
        onClick={(e) => e.stopPropagation()}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
          if (e.key !== "Tab") return;
          const focusables = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>(
              'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
            ),
          ).filter((el) => !el.hasAttribute("disabled"));
          if (focusables.length === 0) return;
          const first = focusables[0]!;
          const last = focusables[focusables.length - 1]!;
          const active = document.activeElement as HTMLElement | null;
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <div className="modal-hd">
          <div className="ic" style={{ background: "var(--brand-wash)", color: "var(--brand-primary)" }}>
            <Icon d={icon} size={20} />
          </div>
          <h3>{title}</h3>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---- Users ----------------------------------------------------------------

interface UserRow extends Row {
  username?: string;
  email?: string;
  lang?: string;
  departmentId?: string;
  groupIds?: string[];
  isActive?: boolean;
}

function UsersTab({ canWrite }: { canWrite: boolean }) {
  const t = useScopedT();
  const { toast, confirm } = useChrome();
  const usersQ = useCollection<UserRow>("users");
  const groupsQ = useCollection("user-groups");
  const deptsQ = useCollection("departments");
  const create = useCreateRow("users");
  const update = useUpdateRow("users");

  const users = usersQ.data ?? [];
  const groups = groupsQ.data ?? [];
  const depts = deptsQ.data ?? [];

  const groupNames = (ids: unknown): string => {
    const arr = Array.isArray(ids) ? (ids as string[]) : [];
    const names = groups.filter((g) => arr.includes(g.id)).map((g) => String(g.name));
    return names.length > 0 ? names.join(", ") : t("access.users.noGroup");
  };

  // Create / edit dialog. `editId === null` ⇒ create mode.
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [lang, setLang] = useState<(typeof LANGS)[number]>("en");
  const [deptId, setDeptId] = useState("");
  const [groupIds, setGroupIds] = useState<string[]>([]);

  const openCreate = () => {
    setEditId(null);
    setName("");
    setUsername("");
    setEmail("");
    setLang("en");
    setDeptId("");
    setGroupIds([]);
    setOpen(true);
  };
  const openEdit = (u: UserRow) => {
    setEditId(u.id);
    setName(String(u.name ?? ""));
    setUsername(String(u.username ?? ""));
    setEmail(String(u.email ?? ""));
    setLang((LANGS as readonly string[]).includes(String(u.lang)) ? (u.lang as (typeof LANGS)[number]) : "en");
    setDeptId(String(u.departmentId ?? ""));
    setGroupIds(Array.isArray(u.groupIds) ? [...u.groupIds] : []);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setEditId(null);
  };

  const toggleGroup = (id: string) =>
    setGroupIds((cur) => (cur.includes(id) ? cur.filter((g) => g !== id) : [...cur, id]));

  const onSubmit = async () => {
    if (!name.trim() || !username.trim()) return;
    // Only fields the `users` schema carries — NO plaintext password (note in UI).
    const body: Record<string, unknown> = {
      name: name.trim(),
      username: username.trim(),
      email: email.trim() || null,
      lang,
      departmentId: deptId || null,
      groupIds,
    };
    try {
      if (editId) {
        await update.mutateAsync({ id: editId, body });
        toast(t("access.users.updated", { name: name.trim() }), "success");
      } else {
        await create.mutateAsync({ ...body, isActive: true });
        toast(t("access.users.created", { name: name.trim() }), "success");
      }
      close();
    } catch {
      toast(t(editId ? "access.users.saveErr" : "access.users.createErr"), "error");
    }
  };

  const toggleActive = async (u: UserRow) => {
    const next = !u.isActive;
    if (!next) {
      const ok = await confirm({
        title: t("access.users.deactivateTitle"),
        body: t("access.users.deactivateConfirm", { name: String(u.name) }),
        confirmLabel: t("access.users.deactivate"),
      });
      if (!ok) return;
    }
    try {
      await update.mutateAsync({ id: u.id, body: { isActive: next } });
      toast(t(next ? "access.users.activated" : "access.users.deactivated", { name: String(u.name) }), "info");
    } catch {
      toast(t("access.users.toggleErr"), "error");
    }
  };

  const valid = name.trim().length > 0 && username.trim().length > 0;

  return (
    <div className="d-card">
      <div className="d-card-hd">
        <h3>{t("access.users.title")}</h3>
        <span className="sp" style={{ flex: 1 }} />
        {canWrite && (
          <Btn variant="primary" size="sm" data-testid="user-add" onClick={openCreate}>
            <Icon d={ICONS.plus} size={15} />
            {t("access.users.add")}
          </Btn>
        )}
      </div>
      <div className="d-card-body" style={{ padding: 0 }}>
        <table className="d-table" data-testid="users-table">
          <thead>
            <tr>
              <th>{t("access.users.col.user")}</th>
              <th>{t("access.users.col.email")}</th>
              <th>{t("access.users.col.group")}</th>
              <th>{t("access.users.col.active")}</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {usersQ.isLoading && <Skeleton variant="table-row" count={3} cols={5} />}
            {!usersQ.isLoading &&
              users.map((u) => (
                <tr key={u.id} className="rowhover" data-testid={`user-row-${u.id}`}>
                  <td>
                    <div style={{ fontWeight: 700 }} data-testid="user-row-name">
                      {String(u.name)}
                    </div>
                    <div className="mono" style={{ fontSize: 12, color: "var(--sys-fg-3)" }}>
                      {String(u.username ?? "")}
                    </div>
                  </td>
                  <td>{String(u.email ?? "—")}</td>
                  <td>{groupNames(u.groupIds)}</td>
                  <td>
                    {u.isActive ? (
                      <span className="chip-status success" data-testid={`user-active-${u.id}`}>
                        <span className="led" />
                        {t("access.users.active")}
                      </span>
                    ) : (
                      <span className="chip-status paused" data-testid={`user-active-${u.id}`}>
                        <span className="led" />
                        {t("access.users.inactive")}
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {canWrite && (
                      <span style={{ display: "inline-flex", gap: 6 }}>
                        <Btn variant="ghost" size="sm" data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)}>
                          {t("access.users.edit")}
                        </Btn>
                        <Btn variant="ghost" size="sm" data-testid={`user-toggle-${u.id}`} onClick={() => void toggleActive(u)}>
                          {u.isActive ? t("access.users.deactivate") : t("access.users.activate")}
                        </Btn>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!usersQ.isLoading && users.length === 0 && (
          <EmptyState
            icon={ICONS.users}
            title={t("access.users.empty")}
            hint={t("access.users.emptyHint")}
            action={canWrite ? { label: t("access.users.add"), onClick: openCreate } : undefined}
            compact
          />
        )}
      </div>

      {open && canWrite && (
        <Modal
          title={editId ? t("access.users.edit") : t("access.users.new")}
          icon={ICONS.user}
          onClose={close}
          testId="user-form"
          width={520}
        >
          <div className="modal-body">
            <div className="d-field">
              <label htmlFor="user-name">{t("access.users.field.name")}</label>
              <div className="field-ctrl">
                <input
                  id="user-name"
                  data-testid="user-name"
                  placeholder={t("access.users.field.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
            </div>
            <div className="d-field">
              <label htmlFor="user-username">{t("access.users.field.username")}</label>
              <div className="field-ctrl">
                <input
                  id="user-username"
                  data-testid="user-username"
                  placeholder={t("access.users.field.usernamePlaceholder")}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
            </div>
            <div className="d-field">
              <label htmlFor="user-email">{t("access.users.field.email")}</label>
              <div className="field-ctrl">
                <input
                  id="user-email"
                  type="email"
                  data-testid="user-email"
                  placeholder="name@iterum.lv"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div className="d-field" style={{ flex: 1 }}>
                <label htmlFor="user-lang">{t("access.users.field.lang")}</label>
                <div className="field-ctrl is-filled">
                  <select id="user-lang" data-testid="user-lang" value={lang} onChange={(e) => setLang(e.target.value as (typeof LANGS)[number])} style={selectStyle}>
                    {LANGS.map((l) => (
                      <option key={l} value={l}>
                        {LANG_LABEL[l]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="d-field" style={{ flex: 1 }}>
                <label htmlFor="user-dept">{t("access.users.field.department")}</label>
                <div className="field-ctrl is-filled">
                  <select id="user-dept" data-testid="user-dept" value={deptId} onChange={(e) => setDeptId(e.target.value)} style={selectStyle}>
                    <option value="">{t("access.users.noDept")}</option>
                    {depts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {String(d.name)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="d-field">
              <label>{t("access.users.field.groups")}</label>
              <div role="group" aria-label={t("access.users.field.groups")} style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {groups.map((g) => {
                  const on = groupIds.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      role="checkbox"
                      aria-checked={on}
                      data-testid={`user-group-${g.id}`}
                      className={`chip-status ${on ? "info" : "paused"}`}
                      style={{ cursor: "pointer", border: 0, font: "inherit" }}
                      onClick={() => toggleGroup(g.id)}
                    >
                      <span className="led" />
                      {String(g.name)}
                    </button>
                  );
                })}
              </div>
              <div className="hint" style={{ fontSize: 12, color: "var(--sys-fg-3)", marginTop: 6 }}>
                {t("access.users.field.groupsHint")}
              </div>
            </div>
            <div className="note" style={{ marginTop: 4 }}>
              <Icon d={ICONS.lock} size={16} />
              <span>{t("access.users.passwordNote")}</span>
            </div>
          </div>
          <div className="modal-foot">
            <Btn variant="ghost" size="sm" onClick={close}>
              {t("common.cancel")}
            </Btn>
            <Btn
              variant="primary"
              size="sm"
              data-testid="user-submit"
              disabled={!valid || create.isPending || update.isPending}
              onClick={() => void onSubmit()}
            >
              {editId ? t("access.users.save") : t("access.users.create")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- Permissions matrix ----------------------------------------------------

interface GroupRow extends Row {
  permissions?: GroupPermissions;
}

function PermsTab({ canWrite }: { canWrite: boolean }) {
  const t = useScopedT();
  const { toast, confirm } = useChrome();
  const groupsQ = useCollection<GroupRow>("user-groups");
  const save = useUpdateGroupPermissions();

  const groupList = groupsQ.data ?? [];
  const [groupId, setGroupId] = useState<string | null>(null);
  useEffect(() => {
    if (!groupId && groupList[0]) setGroupId(groupList[0].id);
  }, [groupList, groupId]);

  const group = groupList.find((g) => g.id === groupId);
  const perms = useMemo<GroupPermissions>(() => {
    const p = group?.permissions;
    return p && p.levels ? p : { levels: {}, visibleCardTabs: [] };
  }, [group]);

  // Local edit buffer so the matrix is responsive before save. We track explicit
  // card-tab overrides separately from levels: by default visibleCardTabs is
  // DERIVED from levels (groupFromLevels), but the admin can override per module.
  const [draftLevels, setDraftLevels] = useState<Partial<Record<PermModule, AccessLevel>> | null>(null);
  const [tabOverrides, setTabOverrides] = useState<Partial<Record<PermModule, boolean>>>({});
  useEffect(() => {
    setDraftLevels(null);
    setTabOverrides({});
  }, [groupId]);

  const effLevels: Partial<Record<PermModule, AccessLevel>> = draftLevels ?? perms.levels;
  const dirty = draftLevels !== null || Object.keys(tabOverrides).length > 0;

  // Effective GroupPermissions = core builder over the levels, then apply the
  // admin's explicit per-module tab overrides on top. This is the EXACT shape
  // saved to the server (so the matrix round-trips through user_groups.permissions).
  const effectivePerms = useMemo<GroupPermissions>(() => {
    const built = groupFromLevels(effLevels);
    const tabs = new Set(built.visibleCardTabs);
    for (const m of PERM_MODULES) {
      const ov = tabOverrides[m];
      if (ov === true) tabs.add(m);
      else if (ov === false) tabs.delete(m);
    }
    return { levels: effLevels, visibleCardTabs: [...tabs] };
  }, [effLevels, tabOverrides]);

  const setLevel = (mod: PermModule, lvl: AccessLevel) => {
    setDraftLevels((cur) => ({ ...(cur ?? perms.levels), [mod]: lvl }));
  };

  const toggleTab = (mod: PermModule) => {
    const current = cardTabVisible(effectivePerms, mod);
    setTabOverrides((cur) => ({ ...cur, [mod]: !current }));
  };

  const onSave = async () => {
    if (!groupId || !group || !dirty) return;
    const ok = await confirm({
      title: t("access.perms.saveTitle"),
      body: t("access.perms.saveConfirm", { name: String(group.name) }),
      danger: false,
      confirmLabel: t("access.perms.save"),
    });
    if (!ok) return;
    try {
      await save.mutateAsync({ id: groupId, permissions: effectivePerms });
      toast(t("access.perms.saved", { name: String(group.name) }), "success");
      setDraftLevels(null);
      setTabOverrides({});
    } catch {
      toast(t("access.perms.saveErr"), "error");
    }
  };

  if (!groupsQ.isLoading && groupList.length === 0) {
    return <EmptyState icon={ICONS.shield} title={t("access.perms.empty")} hint={t("access.perms.emptyHint")} />;
  }

  return (
    <div>
      <div className="perm-head" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <label htmlFor="perm-group" className="perm-lbl" style={{ fontWeight: 600, fontSize: 13 }}>
          {t("access.perms.group")}
        </label>
        <div className="field-ctrl is-filled" style={{ width: 260 }}>
          <select id="perm-group" data-testid="perm-group" value={groupId ?? ""} onChange={(e) => setGroupId(e.target.value)} style={selectStyle}>
            {groupList.map((g) => (
              <option key={g.id} value={g.id}>
                {String(g.name)}
              </option>
            ))}
          </select>
        </div>
        <span style={{ flex: 1 }} />
        {canWrite && (
          <Btn variant="primary" size="sm" data-testid="perm-save" disabled={!dirty || save.isPending} onClick={() => void onSave()}>
            <Icon d={ICONS.check} size={15} />
            {t("access.perms.save")}
          </Btn>
        )}
      </div>

      {groupsQ.isLoading ? (
        <Skeleton variant="card" count={6} height={140} />
      ) : (
        <div
          className="perm-grid"
          data-testid="perm-grid"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}
        >
          {PERM_MODULES.map((mod) => {
            const cur = (effLevels[mod] ?? ACCESS.NONE) as AccessLevel;
            const visible = cardTabVisible(effectivePerms, mod);
            return (
              <div className="perm-card d-card" key={mod} data-testid={`perm-card-${mod}`} style={{ padding: 14 }}>
                <div className="perm-card-name" style={{ fontWeight: 700, marginBottom: 10 }}>
                  {MODULE_LABEL[mod]}
                </div>
                <div
                  className="perm-levels seg"
                  role="radiogroup"
                  aria-label={t("access.perms.levelFor", { module: MODULE_LABEL[mod] })}
                  style={{ marginBottom: 10 }}
                >
                  {LEVELS.map((lvl) => (
                    <button
                      key={lvl}
                      role="radio"
                      aria-checked={cur === lvl}
                      aria-pressed={cur === lvl}
                      aria-label={`${MODULE_LABEL[mod]}: ${levelName(lvl, mod)}`}
                      data-testid={`perm-${mod}-${lvl}`}
                      disabled={!canWrite}
                      className={cur === lvl ? "on" : ""}
                      onClick={() => setLevel(mod, lvl)}
                    >
                      {levelName(lvl, mod)}
                    </button>
                  ))}
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--sys-fg-2)" }}>
                  <button
                    className={`toggle ${visible ? "on" : ""}`}
                    role="switch"
                    aria-checked={visible}
                    aria-label={t("access.perms.menuVisibleFor", { module: MODULE_LABEL[mod] })}
                    data-testid={`perm-tab-${mod}`}
                    disabled={!canWrite}
                    onClick={() => toggleTab(mod)}
                    style={toggleStyle(visible)}
                  >
                    <span className="knob" />
                  </button>
                  {t("access.perms.menuVisible")}
                </label>
              </div>
            );
          })}
        </div>
      )}

      <div className="note" style={{ marginTop: 18 }}>
        <Icon d={ICONS.shield} size={16} />
        <span>{t("access.perms.note")}</span>
      </div>
    </div>
  );
}

function toggleStyle(on: boolean) {
  return {
    width: 38,
    height: 22,
    borderRadius: 999,
    border: 0,
    cursor: "pointer",
    padding: 2,
    background: on ? "var(--brand-primary-fill)" : "var(--sys-bg-sunken)",
    display: "inline-flex",
    justifyContent: on ? "flex-end" : "flex-start",
    alignItems: "center",
    transition: "background .15s",
  } as const;
}

// ---- Companies -------------------------------------------------------------

interface CompanyRow extends Row {
  currency?: string;
  active?: boolean;
}

function CompaniesTab({ canWrite }: { canWrite: boolean }) {
  const t = useScopedT();
  const { toast } = useChrome();
  const companiesQ = useCollection<CompanyRow>("companies");
  const create = useCreateRow("companies");
  const update = useUpdateRow("companies");

  const companies = companiesQ.data ?? [];

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("");

  const openCreate = () => {
    setEditId(null);
    setName("");
    setCurrency("");
    setOpen(true);
  };
  const openEdit = (c: CompanyRow) => {
    setEditId(c.id);
    setName(String(c.name ?? ""));
    setCurrency(String(c.currency ?? ""));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setEditId(null);
  };

  const onSubmit = async () => {
    if (!name.trim()) return;
    const body = { name: name.trim(), currency: currency.trim() || null };
    try {
      if (editId) {
        await update.mutateAsync({ id: editId, body });
        toast(t("access.companies.updated", { name: name.trim() }), "success");
      } else {
        await create.mutateAsync({ ...body, active: true });
        toast(t("access.companies.created", { name: name.trim() }), "success");
      }
      close();
    } catch {
      toast(t("access.companies.saveErr"), "error");
    }
  };

  return (
    <div className="d-card">
      <div className="d-card-hd">
        <h3>{t("access.companies.title")}</h3>
        <span className="dict-hint">{t("access.companies.tenant")}</span>
        <span className="sp" style={{ flex: 1 }} />
        {canWrite && (
          <Btn variant="primary" size="sm" data-testid="company-add" onClick={openCreate}>
            <Icon d={ICONS.plus} size={15} />
            {t("access.companies.add")}
          </Btn>
        )}
      </div>
      <div className="d-card-body" style={{ padding: 0 }}>
        <table className="d-table" data-testid="companies-table">
          <thead>
            <tr>
              <th>{t("access.companies.col.name")}</th>
              <th>{t("access.companies.col.currency")}</th>
              <th>{t("access.companies.col.active")}</th>
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {companiesQ.isLoading && <Skeleton variant="table-row" count={2} cols={4} />}
            {!companiesQ.isLoading &&
              companies.map((c) => (
                <tr key={c.id} className="rowhover" data-testid={`company-row-${c.id}`}>
                  <td style={{ fontWeight: 700 }} data-testid="company-row-name">
                    {String(c.name)}
                  </td>
                  <td className="mono">{String(c.currency ?? "—")}</td>
                  <td>
                    {c.active ? (
                      <Icon d={ICONS.check} size={16} style={{ color: "var(--status-success)" }} />
                    ) : (
                      <Icon d={ICONS.x} size={15} style={{ color: "var(--sys-fg-mute)" }} />
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {canWrite && (
                      <Btn variant="ghost" size="sm" data-testid={`company-edit-${c.id}`} onClick={() => openEdit(c)}>
                        {t("access.companies.edit")}
                      </Btn>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {!companiesQ.isLoading && companies.length === 0 && (
          <EmptyState
            icon={ICONS.building}
            title={t("access.companies.empty")}
            hint={t("access.companies.emptyHint")}
            action={canWrite ? { label: t("access.companies.add"), onClick: openCreate } : undefined}
            compact
          />
        )}
      </div>

      {open && canWrite && (
        <Modal
          title={editId ? t("access.companies.edit") : t("access.companies.new")}
          icon={ICONS.building}
          onClose={close}
          testId="company-form"
        >
          <div className="modal-body">
            <div className="d-field">
              <label htmlFor="company-name">{t("access.companies.field.name")}</label>
              <div className="field-ctrl">
                <input
                  id="company-name"
                  data-testid="company-name"
                  placeholder={t("access.companies.field.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void onSubmit();
                  }}
                />
              </div>
            </div>
            <div className="d-field">
              <label htmlFor="company-currency">{t("access.companies.field.currency")}</label>
              <div className="field-ctrl">
                <input
                  id="company-currency"
                  data-testid="company-currency"
                  placeholder={t("access.companies.field.currencyPlaceholder")}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <Btn variant="ghost" size="sm" onClick={close}>
              {t("common.cancel")}
            </Btn>
            <Btn variant="primary" size="sm" data-testid="company-submit" disabled={!name.trim() || create.isPending || update.isPending} onClick={() => void onSubmit()}>
              {editId ? t("access.companies.save") : t("access.companies.create")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---- Departments (hierarchy tree) ------------------------------------------

interface DeptRow extends Row {
  parentDepartmentId?: string;
}

/** Order departments depth-first under their parents, carrying a depth for indent. */
function buildDeptTree(depts: DeptRow[]): { row: DeptRow; depth: number }[] {
  const byParent = new Map<string, DeptRow[]>();
  const ids = new Set(depts.map((d) => d.id));
  const roots: DeptRow[] = [];
  for (const d of depts) {
    const parent = d.parentDepartmentId;
    // Treat a missing/dangling parent as a root so nothing is hidden.
    if (parent && ids.has(parent)) {
      const arr = byParent.get(parent) ?? [];
      arr.push(d);
      byParent.set(parent, arr);
    } else {
      roots.push(d);
    }
  }
  const out: { row: DeptRow; depth: number }[] = [];
  const visited = new Set<string>();
  const walk = (node: DeptRow, depth: number) => {
    if (visited.has(node.id)) return; // guard against cycles
    visited.add(node.id);
    out.push({ row: node, depth });
    for (const child of byParent.get(node.id) ?? []) walk(child, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  // Any node not reached (e.g. inside a cycle) is appended at the top level.
  for (const d of depts) if (!visited.has(d.id)) out.push({ row: d, depth: 0 });
  return out;
}

function DeptsTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const t = useScopedT();
  const { toast, confirm } = useChrome();
  const deptsQ = useCollection<DeptRow>("departments");
  const create = useCreateRow("departments");
  const update = useUpdateRow("departments");
  const remove = useDeleteRow("departments");

  const depts = deptsQ.data ?? [];
  const tree = useMemo(() => buildDeptTree(depts), [depts]);
  const deptName = (id: unknown): string => {
    const d = depts.find((x) => x.id === id);
    return d ? String(d.name) : "—";
  };

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");

  const openCreate = () => {
    setEditId(null);
    setName("");
    setParentId("");
    setOpen(true);
  };
  const openEdit = (d: DeptRow) => {
    setEditId(d.id);
    setName(String(d.name ?? ""));
    setParentId(String(d.parentDepartmentId ?? ""));
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setEditId(null);
  };

  // A department may not be its own parent (the edited row is excluded). We do
  // not deep-cycle-check here; the tree builder guards against display cycles.
  const parentOptions = depts.filter((d) => d.id !== editId);

  const onSubmit = async () => {
    if (!name.trim()) return;
    const body = { name: name.trim(), parentDepartmentId: parentId || null };
    try {
      if (editId) {
        await update.mutateAsync({ id: editId, body });
        toast(t("access.depts.updated", { name: name.trim() }), "success");
      } else {
        await create.mutateAsync(body);
        toast(t("access.depts.created", { name: name.trim() }), "success");
      }
      close();
    } catch {
      toast(t("access.depts.saveErr"), "error");
    }
  };

  const onDelete = async (d: DeptRow) => {
    const ok = await confirm({
      title: t("access.depts.deleteTitle"),
      body: t("access.depts.deleteConfirm", { name: String(d.name) }),
      confirmLabel: t("access.depts.delete"),
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(d.id);
      toast(t("access.depts.deleted", { name: String(d.name) }), "info");
    } catch {
      toast(t("access.depts.deleteErr"), "error");
    }
  };

  return (
    <div className="d-card">
      <div className="d-card-hd">
        <h3>{t("access.depts.title")}</h3>
        <span className="dict-hint">{t("access.depts.hint")}</span>
        <span className="sp" style={{ flex: 1 }} />
        {canWrite && (
          <Btn variant="primary" size="sm" data-testid="dept-add" onClick={openCreate}>
            <Icon d={ICONS.plus} size={15} />
            {t("access.depts.add")}
          </Btn>
        )}
      </div>
      <div className="d-card-body" style={{ padding: 0 }}>
        <table className="d-table" data-testid="depts-table">
          <thead>
            <tr>
              <th>{t("access.depts.title")}</th>
              <th style={{ width: 200 }}>{t("access.depts.field.parent")}</th>
              <th style={{ width: 130 }} />
            </tr>
          </thead>
          <tbody>
            {deptsQ.isLoading && <Skeleton variant="table-row" count={3} cols={3} />}
            {!deptsQ.isLoading &&
              tree.map(({ row: d, depth }) => {
                const parent = d.parentDepartmentId;
                return (
                  <tr key={d.id} className="rowhover" data-testid={`dept-row-${d.id}`}>
                    <td style={{ paddingLeft: 14 + depth * 22 }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {depth > 0 && (
                          <Icon d={ICONS.tree} size={13} style={{ color: "var(--sys-fg-mute)" }} />
                        )}
                        <span style={{ fontWeight: depth === 0 ? 700 : 500 }} data-testid="dept-row-name">
                          {String(d.name)}
                        </span>
                      </span>
                    </td>
                    <td>
                      {parent ? (
                        <span className="chip-status info">
                          <span className="led" />
                          {deptName(parent)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {canWrite && (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <Btn variant="ghost" size="sm" data-testid={`dept-edit-${d.id}`} onClick={() => openEdit(d)}>
                            {t("access.depts.edit.btn")}
                          </Btn>
                          {canDelete && (
                            <Btn variant="ghost" size="sm" data-testid={`dept-delete-${d.id}`} onClick={() => void onDelete(d)}>
                              <Icon d={ICONS.trash} size={15} />
                            </Btn>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        {!deptsQ.isLoading && depts.length === 0 && (
          <EmptyState
            icon={ICONS.tree}
            title={t("access.depts.empty")}
            hint={t("access.depts.emptyHint")}
            action={canWrite ? { label: t("access.depts.add"), onClick: openCreate } : undefined}
            compact
          />
        )}
      </div>

      {open && canWrite && (
        <Modal
          title={editId ? t("access.depts.edit") : t("access.depts.new")}
          icon={ICONS.tree}
          onClose={close}
          testId="dept-form"
        >
          <div className="modal-body">
            <div className="d-field">
              <label htmlFor="dept-name">{t("access.depts.field.name")}</label>
              <div className="field-ctrl">
                <input
                  id="dept-name"
                  data-testid="dept-name"
                  placeholder={t("access.depts.field.namePlaceholder")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void onSubmit();
                  }}
                />
              </div>
            </div>
            <div className="d-field">
              <label htmlFor="dept-parent">{t("access.depts.field.parent")}</label>
              <div className="field-ctrl is-filled">
                <select id="dept-parent" data-testid="dept-parent" value={parentId} onChange={(e) => setParentId(e.target.value)} style={selectStyle}>
                  <option value="">{t("access.depts.noParent")}</option>
                  {parentOptions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {String(d.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-foot">
            <Btn variant="ghost" size="sm" onClick={close}>
              {t("common.cancel")}
            </Btn>
            <Btn variant="primary" size="sm" data-testid="dept-submit" disabled={!name.trim() || create.isPending || update.isPending} onClick={() => void onSubmit()}>
              {editId ? t("access.depts.save") : t("access.depts.create")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
